import { currencySymbol } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageFieldRenderer } from "@/components/ImageFieldRenderer";
import { FileFieldRenderer } from "@/components/FileFieldRenderer";
import { MemberFieldRenderer } from "@/components/MemberFieldRenderer";
import { ChecklistFieldRenderer } from "@/components/ChecklistFieldRenderer";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

/**
 * Renders a single input for a field_definition.
 *
 * Props:
 *  - field: field_definition object
 *  - value: current value
 *  - onChange(next): update handler
 *  - error: string | null
 */
export function DynamicField({ field, value, onChange, error }) {
  // image/file get bespoke renderers (with upload/preview).
  if (field.type === "image") {
    return <ImageFieldRenderer field={field} value={value} onChange={onChange} error={error} />;
  }
  if (field.type === "file") {
    return <FileFieldRenderer field={field} value={value} onChange={onChange} error={error} />;
  }
  if (field.type === "member") {
    return <MemberFieldRenderer field={field} value={value} onChange={onChange} error={error} />;
  }
  if (field.type === "checklist") {
    return <ChecklistFieldRenderer field={field} value={value} onChange={onChange} />;
  }

  const id = `field-${field.key}`;
  const testId = `input-${field.key}`;

  const label = (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {field.label}
        {field.required && (
          <span className="text-destructive ml-1" aria-hidden>
            *
          </span>
        )}
      </Label>
      <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
        {field.type}
        {field.unique ? " · unique" : ""}
      </span>
    </div>
  );

  const errNode = error ? (
    <p
      className="text-xs text-destructive mt-1.5"
      data-testid={`error-${field.key}`}
    >
      {error}
    </p>
  ) : field.help_text ? (
    <p className="text-xs text-muted-foreground mt-1.5">{field.help_text}</p>
  ) : null;

  const renderControl = () => {
    switch (field.type) {
      case "longtext":
      case "richtext":
        return (
          <Textarea
            id={id}
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={field.type === "richtext" ? 5 : 3}
          />
        );
      case "number":
        return (
          <Input
            id={id}
            data-testid={testId}
            type="number"
            step="any"
            value={value ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : e.target.value)
            }
          />
        );
      case "currency":
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
              {currencySymbol()}
            </span>
            <Input
              id={id}
              data-testid={testId}
              type="number"
              step="any"
              className="pl-8"
              value={value ?? ""}
              onChange={(e) =>
                onChange(e.target.value === "" ? null : e.target.value)
              }
            />
          </div>
        );
      case "boolean":
        return (
          <div className="flex items-center gap-2 h-10">
            <Switch
              id={id}
              data-testid={testId}
              checked={!!value}
              onCheckedChange={(v) => onChange(v)}
            />
            <span className="text-sm text-muted-foreground">
              {value ? "Yes" : "No"}
            </span>
          </div>
        );
      case "date":
        return (
          <DatePicker
            id={id}
            testId={testId}
            value={value ?? null}
            required={field.required}
            onChange={(v) => onChange(v)}
          />
        );
      case "datetime":
        return (
          <DateTimePicker
            id={id}
            testId={testId}
            value={value ?? null}
            required={field.required}
            onChange={(v) => onChange(v)}
          />
        );
      case "dropdown": {
        const options = (field.config?.options || []).map((o) =>
          typeof o === "string" ? { value: o, label: o } : o,
        );
        return (
          <Select
            value={value ?? ""}
            onValueChange={(v) => onChange(v)}
          >
            <SelectTrigger id={id} data-testid={testId}>
              <SelectValue placeholder="Choose an option" />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`option-${field.key}-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case "multi_select": {
        const options = (field.config?.options || []).map((o) =>
          typeof o === "string" ? { value: o, label: o } : o,
        );
        const selected = Array.isArray(value) ? value : [];
        const toggle = (v) => {
          if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
          else onChange([...selected, v]);
        };
        return (
          <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3" data-testid={testId}>
            {options.length === 0 && (
              <span className="text-xs text-muted-foreground col-span-2">
                No options configured
              </span>
            )}
            {options.map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={selected.includes(o.value)}
                  onCheckedChange={() => toggle(o.value)}
                  data-testid={`multi-${field.key}-${o.value}`}
                />
                {o.label}
              </label>
            ))}
          </div>
        );
      }
      case "email":
      case "url":
      case "phone":
      case "text":
        return (
          <Input
            id={id}
            data-testid={testId}
            type={
              field.type === "email"
                ? "email"
                : field.type === "url"
                ? "url"
                : field.type === "phone"
                ? "tel"
                : "text"
            }
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "image":
      case "file":
      case "relation":
        return (
          <Input
            id={id}
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`(${field.type} — stub input; will render inline in Sub-pass B+)`}
          />
        );
      default:
        // Guard: warn loudly if a date/datetime slipped past the case above and
        // fell through to a plain Input. Should never happen now, but this
        // catches accidental regressions.
        if (field.type === "date" || field.type === "datetime") {
          console.warn(
            `[UBOS DynamicField] fell back to native input for field.type="${field.type}" (${field.key}). ` +
            "This is a regression — the switch above should have handled it via DatePicker/DateTimePicker.",
          );
        }
        return (
          <Input
            id={id}
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div data-testid={`field-${field.key}`}>
      {label}
      {renderControl()}
      {errNode}
    </div>
  );
}
